import React, { useCallback } from 'react';
import { ComponentsTypes } from '@/constants/componentsTypes';
import { Components, Component, isField, isCoverageField } from '@/schemas/sceneDataSchema';
import { UseFormReturn } from 'react-hook-form';
import { Layout } from './Layout';
import { DynamicFormFieldRenderer } from './DynamicFormFieldRenderer';
import { DisplayElementsRenderer } from './DisplayElementsRenderer';
import { DynamicCoverageFieldRenderer } from './DynamicCoverageFieldRenderer.tsx';
import { RouteFn } from '@/hooks/use-flow';
import { ExpandableList } from './ExpandableList';
import { ExpandableContainer } from './ExpandableContainer';
import { DynamicLayout } from './DynamicLayout';
import { FormValues } from '@/constants/formTypes';
import { DynamicForm } from './DynamicForm';
import { Carousel } from './Carousel';
import { CarouselTile } from './CarouselTile';
import { ModalDialog } from './ModalDialog';
import { DynamicActionForm } from './DynamicActionForm';
import { RichText } from './RichText';
import { reportMessage } from '@/services/error-reporting-service';
import { Card } from './Card';
import { CardContent } from './CardContent';
import { List } from './List';
import { ReadMore } from './ReadMore';
import { AccordionList } from './AccordionList.tsx';
import { Accordion } from './Accordion';
import { FixedContainer } from './FixedContainer';
import { CoverageSceneWrapper } from './CoverageSceneWrapper';
import { TabPanel } from './TabPanel';
import { TabList } from './TabList';
import { Tab } from './Tab';
import { BindForm } from './BindForm.tsx';
import { SectionContainer } from './SectionContainer.tsx';

interface DynamicRendererProps {
  components: Components;
  route: RouteFn;
  submit?: (data: FormValues) => void | Promise<void>;
  form?: UseFormReturn;
}

export const DynamicRenderer: React.FC<DynamicRendererProps> = React.memo(({ components, route, submit, form }) => {
  const renderElement = useCallback(
    (component: Component, index: number) => {
      if (isField(component) && form) {
        return (
          <DynamicFormFieldRenderer
            key={index}
            form={form}
            component={component}
          />
        );
      }
      if (isCoverageField(component)) {
        return (
          <DynamicCoverageFieldRenderer
            key={index}
            component={component}
          />
        );
      }
      return (
        <DisplayElementsRenderer
          key={index}
          component={component}
          form={form}
          route={route}
        />
      );
    },
    [form, route]
  );

  /* eslint-disable react-hooks/immutability */
  const renderComponents = useCallback(
    (components: Components) => {
      return components.map((component: Component, index: number) => {
        switch (component.type) {
          case ComponentsTypes.CARD:
            return (
              <Card
                key={index}
                component={component}
              >
                {renderComponents(component.components)}
              </Card>
            );
          case ComponentsTypes.SECTION_CONTAINER:
            return (
              <SectionContainer
                key={index}
                id={component.id}
              >
                {renderComponents(component.components)}
              </SectionContainer>
            );
          case ComponentsTypes.MOCK_DATA_WRAPPER:
            return (
              <CoverageSceneWrapper
                key={index}
                initial_state={component.initial_state}
              >
                {renderComponents(component.components)}
              </CoverageSceneWrapper>
            );
          case ComponentsTypes.COVERAGE_SCENE_WRAPPER:
            return (
              <CoverageSceneWrapper
                key={index}
                initial_state={component.initial_state}
              >
                {renderComponents(component.components)}
              </CoverageSceneWrapper>
            );
          case ComponentsTypes.CARD_CONTENT:
            return (
              <CardContent
                key={index}
                id={component.id}
                component={component}
              >
                {renderComponents(component.components)}
              </CardContent>
            );
          case ComponentsTypes.LAYOUT:
            return (
              <Layout
                key={index}
                id={component.id}
                direction={component.direction}
                spacing={component.spacing}
                align_items={component.align_items}
                justify_content={component.justify_content}
                media_query={component.media_query}
                padding={component.padding}
              >
                {renderComponents(component.components)}
              </Layout>
            );
          case ComponentsTypes.BIND_FORM:
            return (
              <BindForm
                key={index}
                formData={component}
                route={route}
              />
            );
          case ComponentsTypes.FORM:
            if (submit && route) {
              return (
                <DynamicForm
                  key={index}
                  formData={component}
                  onSubmit={submit}
                  route={route}
                />
              );
            }

            reportMessage({
              message: 'DynamicRenderer: DynamicForm component requires onSubmit and route props',
            });
            return null;
          case ComponentsTypes.ACTION_FORM:
            if (route) {
              return (
                <DynamicActionForm
                  key={index}
                  formData={component}
                  route={route}
                />
              );
            }

            reportMessage({
              message: 'DynamicRenderer: DynamicForm component requires route props',
            });
            return null;
          case ComponentsTypes.MODAL:
            return (
              <ModalDialog
                key={index}
                status={component.status}
                id={component.id}
                sticky_footer={component.sticky_footer}
                footerChildren={
                  component.modal_footer && (
                    <DynamicRenderer
                      components={component.modal_footer}
                      route={route}
                      form={form}
                    />
                  )
                }
              >
                {renderComponents(component.modal_body)}
              </ModalDialog>
            );
          case ComponentsTypes.ACCORDION_LIST:
            return <AccordionList key={index}>{renderComponents(component.accordions)}</AccordionList>;
          case ComponentsTypes.ACCORDION:
            return (
              <Accordion
                key={index}
                id={component.id}
                summary={component.summary}
              >
                {renderComponents(component.components)}
              </Accordion>
            );
          case ComponentsTypes.TAB:
            return (
              <Tab
                key={index}
                label={component.label}
                tabId={component.tab_id}
              />
            );
          case ComponentsTypes.TAB_LIST:
            return (
              <TabList
                key={index}
                tabListId={component.tab_list_id}
              >
                {renderComponents(component.tabs)}
              </TabList>
            );
          case ComponentsTypes.TAB_PANEL:
            return (
              <TabPanel
                key={index}
                tabId={component.tab_id}
                tabListId={component.tab_list_id}
              >
                {renderComponents(component.components)}
              </TabPanel>
            );
          case ComponentsTypes.CAROUSEL:
            return (
              <Carousel
                padding={component.padding}
                key={index}
              >
                {renderComponents(component.tiles)}
              </Carousel>
            );
          case ComponentsTypes.CAROUSEL_TILE:
            return (
              <CarouselTile
                key={index}
                id={component.id}
                padding={component.padding}
              >
                {renderComponents(component.components)}
              </CarouselTile>
            );
          case ComponentsTypes.EXPANDABLE_LIST:
            return <ExpandableList key={index}>{renderComponents(component.components)}</ExpandableList>;
          case ComponentsTypes.EXPANDABLE_CONTAINER:
            return (
              <ExpandableContainer
                key={index}
                summary={renderComponents(component.summary)}
                details={renderComponents(component.details)}
              />
            );
          case ComponentsTypes.DYNAMIC_LAYOUT:
            return (
              <DynamicLayout
                key={index}
                header={renderComponents(component.header)}
                main={renderComponents(component.main)}
                aside={renderComponents(component.aside)}
              />
            );
          case ComponentsTypes.READ_MORE:
            return <ReadMore key={index}>{renderComponents(component.components)}</ReadMore>;
          case ComponentsTypes.FIXED_CONTAINER:
            return <FixedContainer key={index}>{renderComponents(component.components)}</FixedContainer>;
          case ComponentsTypes.LIST:
            return (
              <List
                key={index}
                id={component.id}
                listType={component.list_type}
                color={component.color}
              >
                {renderComponents(component.components)}
              </List>
            );
          case ComponentsTypes.RICH_TEXT:
            return (
              <RichText
                key={index}
                variant={component.variant}
                spans={component.spans}
                align={component.align}
                route={route}
              />
            );
          default:
            return renderElement(component, index);
        }
      });
    },
    [submit, route, form, renderElement]
  );

  return renderComponents(components);
});
